import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { Button, Col, Row } from 'reactstrap';
import Content from '../../../layout/Content';
import Table from '../../../common/Table';
import DriverTaskSummaryBaseForm, {
  DRIVER_TASK_SUMMARY_BASE_FORM,
} from './DriverTaskSummaryBaseForm';
import DriverTaskSummaryMovementsForm from './DriverTaskSummaryMovementsForm';
import DriverTaskSummaryMonthlyForm from './DriverTaskSummaryMonthlyForm';
import {
  DRIVER_TASK_SUMMARY_MONTHLY_COLUMNS,
  TASK_SUMMARY_MOVEMENT_COLUMNS,
} from '../../../../config/columns';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import { DRIVER_TASK_SUMMARY_PATH } from '../../../../config/paths';
import {
  clearDriverTaskSummary,
  exportDriverTaskSummaryMonthly,
  getDriverTaskSummaryMonthly,
  getDriverTaskSummaryMovements,
} from '../../../../actions/traffic/DriverTaskSummary';
import SummaryGauge from '../../../common/gauge/SummaryGauge';
import '../../../common/gauge/GaugeChart.css';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';

const baseSelector = formValueSelector(DRIVER_TASK_SUMMARY_BASE_FORM);

const buildSelectValues = (options) =>
  options && options.length > 0 ? options.map((option) => option.value) : null;

const buildCommonFilters = (homeBaseLocation, driverStatusType) => {
  const filters = {};

  const homeBaseLocationIds = buildSelectValues(homeBaseLocation);
  if (homeBaseLocationIds) {
    filters.homeBaseLocationIds = homeBaseLocationIds;
  }

  const driverStatusTypes = buildSelectValues(driverStatusType);
  if (driverStatusTypes) {
    filters.driverStatusTypes = driverStatusTypes;
  }

  return filters;
};

const DriverTaskSummary = ({
  breadcrumbs,
  homeBaseLocation,
  driverStatusType,
  movements,
  monthly,
  loadingMovements,
  loadingMonthly,
  dispatchGetDriverTaskSummaryMovements,
  dispatchGetDriverTaskSummaryMonthly,
  dispatchClearDriverTaskSummary,
  dispatchExportDriverTaskSummaryMonthly,
}) => {
  const [lastMonthlyQuery, setLastMonthlyQuery] = useState(null);

  useEffect(
    () => () => {
      dispatchClearDriverTaskSummary();
    },
    [],
  );

  const onMovementsSubmit = (formValues) => {
    const query = {
      fromDateString: formValues.fromDateString,
      toDateString: formValues.toDateString,
      ...buildCommonFilters(homeBaseLocation, driverStatusType),
    };

    dispatchGetDriverTaskSummaryMovements(query);
  };

  const onMonthlySubmit = (formValues) => {
    const query = {
      fromMonthString: formValues.fromMonthString,
      ...buildCommonFilters(homeBaseLocation, driverStatusType),
    };

    if (formValues.toMonthString) {
      query.toMonthString = formValues.toMonthString;
    }

    setLastMonthlyQuery(query);
    dispatchGetDriverTaskSummaryMonthly(query);
  };

  const movementsData = useMemo(
    () => (movements ? movements.toJS() : []),
    [movements],
  );

  const monthlyData = useMemo(() => (monthly ? monthly.toJS() : []), [monthly]);

  const movementsTotal = useMemo(
    () => movementsData.reduce((acc, item) => acc + (item.total || 0), 0),
    [movementsData],
  );

  const buildTotals = (rows) =>
    (rows || []).reduce(
      (acc, row) => ({
        vacations: acc.vacations + (row.vacations || 0),
        paidVacations: acc.paidVacations + (row.paidVacations || 0),
        generatedPhysicalRest:
          acc.generatedPhysicalRest + (row.generatedPhysicalRest || 0),
        takenPhysicalRest: acc.takenPhysicalRest + (row.takenPhysicalRest || 0),
        generatedVacationDays: acc.generatedVacationDays + (row.generatedVacationDays || 0),
        accumulatedVacations: acc.accumulatedVacations + (row.accumulatedVacations || 0),
        accumulatedPaidVacations: acc.accumulatedPaidVacations + (row.accumulatedPaidVacations || 0),
      }),
      {
        vacations: 0,
        paidVacations: 0,
        generatedPhysicalRest: 0,
        takenPhysicalRest: 0,
        generatedVacationDays: 0,
        accumulatedVacations: 0,
        accumulatedPaidVacations: 0
      },
    );

  const totals = useMemo(() => buildTotals(monthlyData), [monthlyData]);

  const content = (
    <>
      <Row>
        <Col md="12">
          <h2>Base</h2>
          <DriverTaskSummaryBaseForm />
        </Col>
      </Row>

      <hr />

      <Row>
        <Col>
          <h2>Resumen de Movimientos</h2>
          <DriverTaskSummaryMovementsForm onSubmit={onMovementsSubmit} />
        </Col>
        <Col>
          <Table
            columns={TASK_SUMMARY_MOVEMENT_COLUMNS}
            data={movementsData}
            loading={loadingMovements}
            manual={false}
            showPagination={false}
            defaultPageSize={
              movementsData.length > 0 ? movementsData.length : 13
            }
          />

          {movementsData.length > 0 && (
            <div className="d-flex justify-content-between font-weight-bold border-top pt-2 mt-2">
              <span>Total</span>
              <span>{movementsTotal}</span>
            </div>
          )}
        </Col>
      </Row>

      <hr />

      <Row>
        <Col md="12">
          <h2>Resumen Mensual</h2>
          <DriverTaskSummaryMonthlyForm onSubmit={onMonthlySubmit} />
          <Col className="d-flex justify-content-center">
            <Row className="flex-grow-1 justify-content-center">
              <Col md="6">
                <SummaryGauge
                  id="vacation-gauge"
                  title="Vacaciones"
                  value1={totals.accumulatedVacations + totals.accumulatedPaidVacations}
                  value2={totals.generatedVacationDays}
                  loading={loadingMonthly}
                  legendFormatter={(value, total) =>
                    `${value} cumplida(s) de ${total} total`
                  }
                />
              </Col>

              <Col md="6">
                <SummaryGauge
                  id="physical-rest-gauge"
                  title="Descanso Físico"
                  value1={totals.takenPhysicalRest}
                  value2={totals.generatedPhysicalRest - totals.takenPhysicalRest}
                  loading={loadingMonthly}
                  legendFormatter={(value, total) =>
                    `${value} gozado(s) de ${total} generado(s)`
                  }
                />
              </Col>
            </Row>
          </Col>
          <div className="d-flex justify-content-end mb-3">
            <Button
              color="success"
              size="md"
              className="flex-end"
              disabled={!lastMonthlyQuery || loadingMonthly}
              onClick={() =>
                dispatchExportDriverTaskSummaryMonthly(lastMonthlyQuery)
              }
            >
              <i className="fa fa-file-excel-o mr-2" />
              Exportar a Excel
            </Button>
          </div>
          <Table
            columns={DRIVER_TASK_SUMMARY_MONTHLY_COLUMNS}
            data={monthlyData}
            loading={loadingMonthly}
            manual={false}
            filterable
            defaultPageSize={20}
            defaultFilterMethod={filterMethodCaseInsensitive}
          />
        </Col>
      </Row>
    </>
  );

  return (
    <Content
      title="Resumen de Tareo Conductores"
      breadcrumbs={breadcrumbs}
      content={content}
      subtitle="Los filtros base y estado afecta a toda la vista"
    />
  );
};

DriverTaskSummary.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  homeBaseLocation: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
  ),
  driverStatusType: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  movements: PropTypes.instanceOf(Immutable.List),
  monthly: PropTypes.instanceOf(Immutable.List),
  loadingMovements: PropTypes.bool,
  loadingMonthly: PropTypes.bool,
  dispatchGetDriverTaskSummaryMovements: PropTypes.func.isRequired,
  dispatchGetDriverTaskSummaryMonthly: PropTypes.func.isRequired,
  dispatchClearDriverTaskSummary: PropTypes.func.isRequired,
  dispatchExportDriverTaskSummaryMonthly: PropTypes.func.isRequired,
};

DriverTaskSummary.defaultProps = {
  homeBaseLocation: [],
  driverStatusType: [],
  movements: null,
  monthly: null,
  loadingMovements: false,
  loadingMonthly: false,
};

const mapStateToProps = (state) => ({
  breadcrumbs: [
    ...state.TrafficUnit.DriverUnitHome.get('breadcrumbs'),
    {
      text: 'Resumen de Tareo Conductores',
      href: DRIVER_TASK_SUMMARY_PATH,
    },
  ],
  homeBaseLocation: baseSelector(state, 'homeBaseLocation'),
  driverStatusType: baseSelector(state, 'driverStatusType'),
  movements: state.TrafficUnit.DriverTaskSummary.getIn([
    'movements',
    'content',
  ]),
  monthly: state.TrafficUnit.DriverTaskSummary.getIn(['monthly', 'content']),
  loadingMovements: state.TrafficUnit.DriverTaskSummary.getIn([
    'movements',
    'loading',
  ]),
  loadingMonthly: state.TrafficUnit.DriverTaskSummary.getIn([
    'monthly',
    'loading',
  ]),
});

const mapDispatchToProps = {
  dispatchGetDriverTaskSummaryMovements: getDriverTaskSummaryMovements,
  dispatchGetDriverTaskSummaryMonthly: getDriverTaskSummaryMonthly,
  dispatchClearDriverTaskSummary: clearDriverTaskSummary,
  dispatchExportDriverTaskSummaryMonthly: exportDriverTaskSummaryMonthly,
};

export default connect(mapStateToProps, mapDispatchToProps)(DriverTaskSummary);
